import { FC, useState, useRef, useEffect, ChangeEvent, useCallback, KeyboardEvent } from "react";
import { CustomCSSProperties, ErrorInfo, Falsy, Id } from "../../types";
import { Icon } from "../Icon";
import { _ } from "../../utils/helpers";
import { timeOuts } from "../../utils/symbols";

export type SelectOption = {
    value: Id, 
    name: string, 
    flag?: string,
}

export type SelectEvent = {
    id: Id
    option: SelectOption
}

interface InputSelectProps {
    id: string
    options: SelectOption[]
    value: Id
    onSelect: (selectedOption: SelectEvent) => void
    placeholder: string
    getError?: (value: Id | Falsy) => Falsy | ErrorInfo
    searchable?: boolean
}

export const InputSelect: FC<InputSelectProps> = ({id, options, value, onSelect, searchable, placeholder, getError}) => {

    const [search, setSearch] = useState<string>('')
    const select = useRef<HTMLDivElement | null>(null)
    const optionsList = useRef<HTMLUListElement | null>(null)
    const searchInput = useRef<HTMLInputElement | null>(null)
    const opened = useRef<number>(1000)
    const focusedIndex = useRef<number>(0)
    const errorRef = useRef<HTMLSpanElement | null>(null)
    const hasError = useRef<Falsy | ErrorInfo>(false)

    const computeOptions = (value: string) => options
        .filter(option => option.name.toLocaleLowerCase().startsWith(value))

    let computedOptions = computeOptions(search)
    const isEmpty = !Boolean(value)

    const handleClickOutside = (event: MouseEvent) => {
        if (!select.current!.contains(event.target as Node)) {
            select.current?.classList.remove('opened')
        }
    }

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        const newSearch = event.target.value.toLocaleLowerCase().trimStart()
        setSearch(newSearch)
        focusedIndex.current = 0
        computedOptions = computeOptions(newSearch)
        forwardFalseEvent()
    }

    const forwardFalseEvent = (event?: SelectEvent) => {
        const eventToSend = event ?? {id, option: computedOptions[focusedIndex.current]}
        if (computedOptions.length > 0 && focusedIndex.current >= 0 && focusedIndex.current <= computedOptions.length - 1) {
            const listItem: HTMLLIElement | null = optionsList.current!.querySelector(`li:nth-child(${focusedIndex.current + 1})`)
            if (listItem && optionsList.current) {
                optionsList.current.scrollTo({
                    behavior: 'smooth',
                    top: listItem.offsetTop - (optionsList.current.offsetHeight - listItem.offsetHeight) / 2,
                }); 
            }
            if (getError) hasError.current = getError(eventToSend.option.value)
            if (!hasError.current) hideError()

        }

        if (eventToSend.option) onSelect(eventToSend)
    }

    const handleKeys = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault()
                event.stopPropagation()
                if (focusedIndex.current > 0) focusedIndex.current -= 1
                forwardFalseEvent()
            break;
            case 'ArrowDown':
                event.preventDefault()
                event.stopPropagation()
                if (focusedIndex.current < computedOptions.length - 1) focusedIndex.current += 1
                forwardFalseEvent()
            break;
            case 'Escape':
                event.preventDefault()
                event.stopPropagation()
                close()
            break;
            default: break;
        }
    }, [computedOptions])

    useEffect(() => {
        window.addEventListener('click', handleClickOutside)
        return () => {
            window.removeEventListener('click', handleClickOutside)
        }
        
    }, [])

    const open = ()  => {
        if (Date.now() - opened.current < 200) return
        opened.current = 0
        searchInput.current?.focus()
        select.current!.classList.add('opened')
        focusedIndex.current = Boolean(value) ? computedOptions.findIndex(option => option.value === value) : -1
    }
    const close = () => {
        searchInput.current?.blur()
        select.current!.classList.remove('opened')
        opened.current = Date.now()
        if (hasError.current) showError()
    }
    const showError = () => {
        if (!hasError.current || !errorRef.current) return
        errorRef.current.innerHTML = _(hasError.current.message)
    }
    const hideError = () => {
        if (!errorRef.current) return
    }

    const selectedOption = options.find(option => option.value === value)
    const valueText = selectedOption ? `${selectedOption.flag} ${selectedOption.name}` : placeholder
    const error = getError ? getError(selectedOption?.value) : false

    return (
        <div 
            ref={select} 
            role="group"
            aria-label={`${_('0ar_count__')}. ${valueText === placeholder ? '' : valueText}`}
            id={`${id}-container`}
            className={`w-100 select p-r ${isEmpty ? 'empty' : ''} ${searchable ? 'sercheable' : ''} ${error ? 'has-error' : ''}`}
            style={{'--items': computedOptions.length + Number(searchable)} as CustomCSSProperties}
            onKeyDown={handleKeys}
            onFocus={open}
            onBlur={(event) => {
                if (event.relatedTarget === null || select.current?.contains(event.relatedTarget)) return
                close()
            }}
            >
            <div 
                className="w-100 df aic jcsb px-7 py-6 value r3 h-50p" 
                role="button"
                aria-label={valueText}
                onClick={(event) => {
                    select.current!.classList.contains('opened') ? close() : open()
                }}
            >
                
                <p className={`w-100 mr-5 text s19-400 ellipsis ${isEmpty ? 'softest' : ''}`}>
                    {valueText}
                </p>
                <Icon icon="chevron" className={`fs-0`} aria-hidden/>
            </div>
            <div className="overlay r3 mt-1 p-a">
                { searchable &&  (
                    <div className="df aic jcsb select-search p-r" aria-hidden>
                        <input 
                            value={search}
                            ref={searchInput}
                            type="text"
                            tabIndex={0}
                            id={`${id}-search`}
                            className="text s19-400 pl-7 pr-4 py-6 w-100 h-50p "
                            placeholder={_('0ge_searc__')}
                            autoComplete="country"
                            onChange={handleSearch}
                        />
                        <Icon icon="search" className="p-a" aria-hidden/>
                    </div>
                )}
                <ul 
                    tabIndex={-1}
                    ref={optionsList}
                    className="df fd-c w100" 
                    style={{'--items': computedOptions.length} as CustomCSSProperties}
                >
                {
                    computedOptions.map(option => 
                        <li 
                            aria-label={`${option.name}. ${value === option.value ? _('0ar_selec__') : _('0ar_notse__')}`}
                            key={option.value} 
                            tabIndex={-1}
                            onClick={(event) => {
                                event.stopPropagation()
                                forwardFalseEvent({ id, option: option })
                                select.current!.classList.remove('opened')
                            }}
                            className={`w-100 px-7 py-6 text s19-400 h-50p  ${value === option.value ? 'selected' : ''}`}
                        >
                            <p className="w-100 ellipsis text" aria-hidden>{`${option.flag} ${option.name}`}</p>
                        </li>
                    )
                }
                </ul>
            </div>
            <span 
                id={`${id}-error`} 
                className="input-error text s18-400 w-100"
            >
                {_('0er_field__')}
            </span>
        </div>
    )
}